import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useEffect, useId, useState } from "react"

import { ErrorApi, pedir } from "@/api/cliente"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Modal } from "@/componentes/ui/Modal"
import { FECHA_MAXIMA, FECHA_MINIMA, fechaFueraDeRango } from "@/lib/fechas"
import type { Asignable } from "./asignables"
import { PRIORIDADES, type PrioridadTarea, TIPOS, type TipoTarea } from "./TarjetaTarea"

/**
 * Alta de una tarea, en un modal.
 *
 * ⚠ **Modal y no un panel desplegable.** Crear una tarea es una decisión con siete
 * campos y soportes: mientras se rellena, el tablero de detrás no se toca. El panel
 * que había antes empujaba las columnas hacia abajo y quien escribía perdía de vista
 * justo lo que estaba mirando para decidir a quién asignarla.
 *
 * ⚠ **No se pide el código.** Lo genera el servidor con su correlativo del año.
 * Ofrecerlo invitaría a teclearlo, y dos personas creando una tarea a la vez
 * elegirían el mismo — el `UNIQUE` lo rechazaría y el segundo perdería lo escrito
 * sin entender por qué.
 *
 * ⚠ **Responsable y fecha son obligatorios.** Lo exige el servidor y aquí se refleja:
 * de las tareas que nacen sin dueño ni plazo salen las que no persigue nadie. «Sin
 * asignar» sigue siendo un ESTADO válido —se llega devolviendo la tarea al montón
 * cuando alguien se va de vacaciones— pero ya no un punto de partida.
 *
 * ⚠ **Primero la tarea, después los soportes.** El adjunto cuelga de la tarea, así
 * que hasta que el servidor no devuelve un `id` no hay dónde ponerlo. Si un soporte
 * falla, **la tarea ya está creada y se dice cuál falló**: cerrar el modal y
 * perderla sería peor que subir el archivo otra vez desde el detalle.
 *
 * ⚠ **Sin actualización optimista**, como el resto del tablero: el servidor puede
 * rechazar el alta y pintar una tarjeta que no existe deja el tablero mintiendo.
 */

type Props = {
  abierto: boolean
  asignables: Asignable[]
  onCerrar: () => void
}

export function DialogoNuevaTarea({ abierto, asignables, onCerrar }: Props) {
  const clienteQuery = useQueryClient()
  const idBase = useId()

  const [titulo, setTitulo] = useState("")
  const [tipo, setTipo] = useState<TipoTarea>("OPERATIVA")
  const [prioridad, setPrioridad] = useState<PrioridadTarea>("MEDIA")
  const [asignadoA, setAsignadoA] = useState<number | null>(null)
  const [venceEn, setVenceEn] = useState("")
  const [descripcion, setDescripcion] = useState("")
  const [adjuntos, setAdjuntos] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])
  const [fallidos, setFallidos] = useState<string[]>([])

  // ⚠ **Se limpia al ABRIR, no al cerrar.** El resultado es el mismo —el formulario
  // siempre aparece en blanco— pero sin el parpadeo: el modal tarda 180 ms en salir,
  // y vaciarlo al cerrar deja ver cómo se borra lo que se acaba de escribir.
  //
  // Y hace falta porque este componente NO se desmonta: vive dentro del `Modal`, que
  // es quien aparece y desaparece. Sin esto, la segunda tarea del día empezaba con
  // los datos de la primera.
  useEffect(() => {
    if (!abierto) return
    setTitulo("")
    setTipo("OPERATIVA")
    setPrioridad("MEDIA")
    setAsignadoA(null)
    setVenceEn("")
    setDescripcion("")
    setAdjuntos([])
    setRechazados([])
    setFallidos([])
  }, [abierto])

  const recibir = (archivos: File[]) => {
    const siguiente = sumar(adjuntos, archivos)
    setAdjuntos(siguiente.adjuntos)
    setRechazados(siguiente.rechazados)
  }

  const crear = useMutation({
    meta: { exito: "Tarea creada" },
    mutationFn: async () => {
      const tarea = await pedir<{ id: number }>("/api/v1/tareas", {
        method: "POST",
        body: JSON.stringify({
          titulo: titulo.trim(),
          tipo,
          prioridad,
          asignado_a: asignadoA,
          // Cadena vacía y «sin fecha» son cosas distintas: el servidor espera `null`.
          vence_en: venceEn === "" ? null : venceEn,
          descripcion: descripcion.trim() === "" ? null : descripcion.trim(),
        }),
      })
      // La subida NO pasa por `api.post`: va como `multipart/form-data` y ese módulo
      // pone `Content-Type: application/json` en todo lo que no sea `FormData`.
      const noSubidos: string[] = []
      for (const adjunto of adjuntos) {
        const formulario = new FormData()
        formulario.append("archivo", adjunto.archivo)
        try {
          await pedir(`/api/v1/tareas/${tarea.id}/adjuntos`, {
            method: "POST",
            body: formulario,
          })
        } catch (e) {
          noSubidos.push(
            `${adjunto.archivo.name}: ${e instanceof ErrorApi ? e.message : "no se pudo subir"}`,
          )
        }
      }
      return noSubidos
    },
    onSuccess: (noSubidos) => {
      clienteQuery.invalidateQueries({ queryKey: ["tareas"] })
      // La tarea existe pase lo que pase con los soportes. Si alguno falló se dice y
      // el modal se queda abierto; si no, se cierra.
      if (noSubidos.length > 0) {
        setFallidos(noSubidos)
        setAdjuntos([])
        return
      }
      onCerrar()
    },
  })

  // Lo que el servidor exige, dicho aquí para no gastar un 422 en enterarse: tres
  // caracteres de título, un responsable, una fecha, y que la fecha exista.
  const listo =
    titulo.trim().length >= 3 && asignadoA !== null && venceEn !== "" && !fechaFueraDeRango(venceEn)

  return (
    <Modal
      abierto={abierto}
      titulo="Nueva tarea"
      descripcion="El código lo asigna el sistema al guardar."
      onCerrar={onCerrar}
    >
      <form
        aria-label="Nueva tarea"
        onSubmit={(e) => {
          e.preventDefault()
          if (listo) crear.mutate()
        }}
        // El pegado se escucha en el formulario entero, no sólo en la descripción:
        // quien acaba de copiar una captura la pega donde tenga el cursor.
        onPaste={(e) => {
          const archivos = archivosPegados(e.clipboardData, adjuntos.length)
          if (archivos.length === 0) return
          e.preventDefault()
          recibir(archivos)
        }}
      >
        <label className="block text-sm" htmlFor={`${idBase}-titulo`}>
          Título *
          <input
            id={`${idBase}-titulo`}
            className="campo mt-1"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Llamar al cliente y enviar la documentación"
          />
        </label>

        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block text-sm" htmlFor={`${idBase}-tipo`}>
            Tipo
            <select
              id={`${idBase}-tipo`}
              className="campo mt-1"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoTarea)}
            >
              {TIPOS.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm" htmlFor={`${idBase}-prioridad`}>
            Prioridad
            <select
              id={`${idBase}-prioridad`}
              className="campo mt-1"
              value={prioridad}
              onChange={(e) => setPrioridad(e.target.value as PrioridadTarea)}
            >
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm" htmlFor={`${idBase}-asignado`}>
            Asignar a *
            <select
              id={`${idBase}-asignado`}
              required
              aria-invalid={asignadoA === null}
              className="campo mt-1"
              value={asignadoA ?? ""}
              onChange={(e) => setAsignadoA(e.target.value === "" ? null : Number(e.target.value))}
            >
              {/* Sin opción «sin asignar»: la lista arranca vacía para que haya que
                  elegir, en vez de ofrecer un valor que el servidor va a rechazar. */}
              <option value="">Elige a alguien</option>
              {asignables.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm" htmlFor={`${idBase}-vence`}>
            Vence el *
            <input
              id={`${idBase}-vence`}
              type="date"
              // ⚠ Acotado por los dos lados: un `type="date"` acepta un año de cinco
              // cifras —`20226-09-10`— sin quejarse, y eso sale a un 422 sobre un
              // formulario que se veía bien. Pasó al crear la primera tarea.
              required
              min={FECHA_MINIMA}
              max={FECHA_MAXIMA}
              aria-invalid={venceEn === "" || fechaFueraDeRango(venceEn)}
              className="campo mt-1 tabular-nums"
              value={venceEn}
              onChange={(e) => setVenceEn(e.target.value)}
            />
            {fechaFueraDeRango(venceEn) && (
              <span className="mt-1 block text-xs" style={{ color: "var(--destructive)" }}>
                Esa fecha no existe. Revisa el año.
              </span>
            )}
          </label>
        </div>

        <label className="mt-3 block text-sm" htmlFor={`${idBase}-descripcion`}>
          Descripción
          <textarea
            id={`${idBase}-descripcion`}
            rows={3}
            className="campo mt-1"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </label>

        <p className="mt-3 text-xs" style={{ color: "var(--fg-muted)" }}>
          * Obligatorio. Una tarea sin responsable ni plazo no la persigue nadie.
        </p>

        <AdjuntosNuevos
          adjuntos={adjuntos}
          rechazados={rechazados}
          onArchivos={recibir}
          onQuitar={(id) => setAdjuntos(adjuntos.filter((a) => a.id !== id))}
        />

        {fallidos.length > 0 && (
          <div role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
            <p className="flex items-center gap-2">
              <AlertCircle size={16} aria-hidden={true} />
              La tarea se creó, pero algún soporte no subió:
            </p>
            <ul className="mt-1 ml-6 list-disc">
              {fallidos.map((motivo) => (
                <li key={motivo}>{motivo}</li>
              ))}
            </ul>
          </div>
        )}

        {crear.isError && (
          <p
            role="alert"
            className="mt-3 flex items-center gap-2 text-sm"
            style={{ color: "var(--destructive)" }}
          >
            <AlertCircle size={16} aria-hidden={true} />
            {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo crear la tarea."}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className="boton-secundario px-3 py-1.5 text-sm">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!listo || crear.isPending}
            className="boton-primario px-3 py-1.5 text-sm disabled:opacity-50"
          >
            {crear.isPending ? "Creando…" : "Crear tarea"}
          </button>
        </div>
      </form>
    </Modal>
  )
}
